import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';

const asArray = (value) => (Array.isArray(value) ? value : []);

const HCODetails = () => {
  const navigate = useNavigate();
  const { hcoId } = useParams();
  const { loading, error, data, execute } = useAPICall();

  const details = useMemo(() => data?.data || data || {}, [data]);

  useEffect(() => {
    const load = async () => {
      try {
        await execute(API_CONFIG.hco.details, null, {
          params: { mdmId: hcoId },
          errorMessage: 'Unable to load HCO details.',
        });
      } catch {
      }
    };

    load();
  }, [execute, hcoId]);

  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <PageBreadcrumbs items={[
          { label: 'HCO Search', path: '/search/hco' },
          { label: 'HCO Details' }
        ]} />
      </Box>
      <Typography sx={{ fontWeight: 600,  color: '#1e293b', mb: 0.25 }}>HCO Details</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Record details for HCO ID: {hcoId}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={() => navigate(`/hco-dcr?mdmId=${hcoId}`)}>
          Open HCO DCR
        </Button>
      </Box>

      {loading ? (
        <Paper sx={{ p: 2, mb: 1.5 }}>
          <Grid container spacing={2}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Skeleton variant="text" width="80%" height={24} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 1.5 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}><Typography variant="body2"><strong>MDM ID:</strong> {details.mdmId || hcoId}</Typography></Grid>
              <Grid item xs={12} md={4}><Typography variant="body2"><strong>Business Name:</strong> {details.name || details.business_name || '-'}</Typography></Grid>
              <Grid item xs={12} md={4}><Typography variant="body2"><strong>Org ID:</strong> {details.orgId || details.org_id || '-'}</Typography></Grid>
              <Grid item xs={12} md={4}><Typography variant="body2"><strong>Status:</strong> {details.status || details.recordStatus || '-'}</Typography></Grid>
              <Grid item xs={12} md={4}><Typography variant="body2"><strong>Source:</strong> {details.source || '-'}</Typography></Grid>
            </Grid>
          </Paper>

          <GlobalMaterialTable
            columns={[
              { header: 'Address', id: 'address', accessorFn: (row) => row.addressLine1 || row.address1 || '-' },
              { header: 'City', id: 'city', accessorFn: (row) => row.city || '-' },
              { header: 'State', id: 'state', accessorFn: (row) => row.state || row.stateCode || '-' },
              { header: 'Zip', id: 'zip', accessorFn: (row) => row.zip || row.zipCd || row.zip5 || '-' },
            ]}
            data={asArray(details.address)}
            emptyMessage="No address details found"
            enablePagination={false}
            enableBottomToolbar={false}
            muiTablePaperProps={{ elevation: 0, sx: { borderRadius: 2 } }}
          />
        </>
      )}
    </Box>
  );
};

export default HCODetails;
